import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../services/apiClient";
import useAuthSession from "./useAuthSession";

const RECENT_SEARCHES_KEY = "dealvoice_recent_searches";
const MAX_RECENT_SEARCHES = 5;

// Client-side in-memory cache for instant 0ms responses on repeated keystrokes
const CLIENT_SEARCH_CACHE = new Map();
const CLIENT_CACHE_TTL_MS = 30000;

function getClientCached(key) {
  const item = CLIENT_SEARCH_CACHE.get(key);
  if (!item) return null;
  if (Date.now() > item.expiresAt) {
    CLIENT_SEARCH_CACHE.delete(key);
    return null;
  }
  return item.data;
}

function setClientCached(key, data) {
  if (CLIENT_SEARCH_CACHE.size >= 100) {
    const oldestKey = CLIENT_SEARCH_CACHE.keys().next().value;
    CLIENT_SEARCH_CACHE.delete(oldestKey);
  }
  CLIENT_SEARCH_CACHE.set(key, { data, expiresAt: Date.now() + CLIENT_CACHE_TTL_MS });
}

function getNextSelectedIndex(currentIndex, totalCount, direction) {
  if (totalCount <= 0) return 0;
  if (direction === "down") {
    return currentIndex < totalCount - 1 ? currentIndex + 1 : 0;
  }
  if (direction === "up") {
    return currentIndex > 0 ? currentIndex - 1 : totalCount - 1;
  }
  return currentIndex;
}

function handleSearchEnterPress({
  selectedIndex,
  suggestions,
  query,
  handleSelectSuggestion,
  saveRecentSearch,
  setIsOpen,
  onFilterTab,
  activeTab,
}) {
  if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
    handleSelectSuggestion(suggestions[selectedIndex]);
    return;
  }

  const trimmedQuery = query.trim();
  if (!trimmedQuery) return;

  saveRecentSearch(trimmedQuery);
  setIsOpen(false);
  if (onFilterTab) {
    onFilterTab(activeTab === "overview" ? "leads" : null, trimmedQuery);
  }
}

/**
 * Ultra High-Speed Multi-Entity Global Search Manager (<15ms response).
 * - Queries dedicated /api/quick-search endpoint with indexed B-tree prefix scan.
 * - Client-side in-memory cache delivers instant 0ms results on backspaces / repeated terms.
 * - Aborts in-flight requests cleanly to prevent race conditions.
 * - Full keyboard navigation (ArrowUp, ArrowDown, Enter, Esc, ⌘K / Ctrl+K).
 */
export function useGlobalSearchManager({ activeTab = "overview", onSelectRecord, onFilterTab } = {}) {
  const { companyCode, isEmployee, employeeId } = useAuthSession();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);

  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.warn("Failed to load recent searches:", e);
    }
  }, []);

  const saveRecentSearch = useCallback((term) => {
    if (!term || typeof term !== "string" || !term.trim()) return;
    const clean = term.trim();
    setRecentSearches((prev) => {
      const filtered = prev.filter((item) => item.toLowerCase() !== clean.toLowerCase());
      const updated = [clean, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  }, []);

  const removeRecentSearch = useCallback((termToRemove) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((item) => item !== termToRemove);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  }, []);

  const clearAllRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (e) {}
  }, []);

  // Global Keyboard Shortcut: ⌘K or Ctrl+K to focus search input
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // Blazing Fast Quick Search Query (<15ms)
  const fetchSuggestions = useCallback(
    async (searchTerm) => {
      const q = (searchTerm || "").trim();
      if (!q || q.length < 2 || !companyCode) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      const cacheKey = `${companyCode}:${isEmployee ? employeeId : "all"}:${q.toLowerCase()}`;
      const clientCached = getClientCached(cacheKey);
      if (clientCached) {
        setSuggestions(clientCached);
        setLoading(false);
        setSelectedIndex(-1);
        return;
      }

      // Abort previous in-flight requests cleanly
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setLoading(true);

      try {
        const res = await apiClient.get("/api/quick-search", {
          params: {
            companyCode,
            q,
            employeeId: isEmployee ? employeeId : undefined,
          },
          headers: { "x-active-company-code": companyCode },
          signal,
        });

        if (signal.aborted) return;

        const results = res?.results || [];
        setClientCached(cacheKey, results);
        setSuggestions(results);
        setSelectedIndex(-1);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
          console.error("[QuickSearch Error]", err);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [companyCode, isEmployee, employeeId]
  );

  // Fast 120ms debounce with client cache check
  const handleQueryChange = (val) => {
    setQuery(val);
    setIsOpen(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = (val || "").trim();
    if (!trimmed || trimmed.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const cacheKey = `${companyCode}:${isEmployee ? employeeId : "all"}:${trimmed.toLowerCase()}`;
    const clientCached = getClientCached(cacheKey);
    if (clientCached) {
      setSuggestions(clientCached);
      setLoading(false);
      setSelectedIndex(-1);
      return;
    }

    setLoading(true);
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, 120);
  };

  // Selection of a search suggestion
  const handleSelectSuggestion = (item) => {
    saveRecentSearch(item.title);
    setIsOpen(false);
    setQuery(item.title);
    if (onSelectRecord) {
      onSelectRecord(item);
    } else if (onFilterTab) {
      onFilterTab(item.targetTab, item.title, item.data);
    }
  };

  // Selection of a recent search term
  const handleSelectRecentSearch = (term) => {
    setQuery(term);
    fetchSuggestions(term);
    if (onFilterTab) {
      onFilterTab(activeTab === "overview" ? "leads" : null, term);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => getNextSelectedIndex(prev, suggestions.length, "down"));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => getNextSelectedIndex(prev, suggestions.length, "up"));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchEnterPress({
        selectedIndex,
        suggestions,
        query,
        handleSelectSuggestion,
        saveRecentSearch,
        setIsOpen,
        onFilterTab,
        activeTab,
      });
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = (e) => {
    if (e.relatedTarget?.closest?.(".global-search-dropdown")) {
      return;
    }
    setTimeout(() => setIsOpen(false), 200);
  };

  return {
    query,
    setQuery,
    suggestions,
    loading,
    isOpen,
    setIsOpen,
    selectedIndex,
    recentSearches,
    inputRef,
    handleQueryChange,
    handleSelectSuggestion,
    handleSelectRecentSearch,
    removeRecentSearch,
    clearAllRecentSearches,
    handleKeyDown,
    handleFocus,
    handleBlur,
  };
}

export default useGlobalSearchManager;
